import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import KPICard from '../../components/reports/KPICard';
import ReportChart from '../../components/reports/ReportChart';
import ReportTable from '../../components/reports/ReportTable';
import BranchSelect from '../../components/reports/BranchSelect';
import { calculateWarningKPIs, getUniqueBranches, filterByBranch, exportToCSV } from '../../utils/reportHelpers';

function ResolvedReport() {
  const navigate = useNavigate();
  const { warnings, customers } = useData();
  
  const [selectedBranch, setSelectedBranch] = useState([]);
  const branches = getUniqueBranches(customers);
  
  let resolvedWarnings = warnings.filter(w => w.status === 'resolved');
  resolvedWarnings = filterByBranch(resolvedWarnings, customers, selectedBranch);
  const kpis = calculateWarningKPIs(resolvedWarnings);

  // Resolution time by risk level
  const resolutionByRisk = {
    labels: ['Cao', 'Trung bình', 'Thấp'],
    datasets: [{
      label: 'Thời gian xử lý TB (ngày)',
      data: [
        resolvedWarnings.filter(w => w.riskLevel === 'high').reduce((sum, w) => {
          if (!w.resolvedDate || !w.createdDate) return sum;
          const days = (new Date(w.resolvedDate) - new Date(w.createdDate)) / (1000 * 60 * 60 * 24);
          return sum + days;
        }, 0) / resolvedWarnings.filter(w => w.riskLevel === 'high').length || 0,
        
        resolvedWarnings.filter(w => w.riskLevel === 'medium').reduce((sum, w) => {
          if (!w.resolvedDate || !w.createdDate) return sum;
          const days = (new Date(w.resolvedDate) - new Date(w.createdDate)) / (1000 * 60 * 60 * 24);
          return sum + days;
        }, 0) / resolvedWarnings.filter(w => w.riskLevel === 'medium').length || 0,
        
        resolvedWarnings.filter(w => w.riskLevel === 'low').reduce((sum, w) => {
          if (!w.resolvedDate || !w.createdDate) return sum;
          const days = (new Date(w.resolvedDate) - new Date(w.createdDate)) / (1000 * 60 * 60 * 24);
          return sum + days;
        }, 0) / resolvedWarnings.filter(w => w.riskLevel === 'low').length || 0
      ].map(v => v.toFixed(1)),
      backgroundColor: ['#dc3545', '#fd7e14', '#ffc107']
    }]
  };

  // Top performers
  const performerMap = {};
  resolvedWarnings.forEach(w => {
    if (w.resolvedBy) {
      performerMap[w.resolvedBy] = (performerMap[w.resolvedBy] || 0) + 1;
    }
  });
  
  const topPerformers = {
    labels: Object.keys(performerMap).slice(0, 5),
    datasets: [{
      label: 'Số cases đã xử lý',
      data: Object.values(performerMap).slice(0, 5),
      backgroundColor: '#20c997'
    }]
  };

  const tableColumns = [
    { key: 'code', label: 'Mã CB', sortable: true },
    { key: 'customerName', label: 'Khách hàng', sortable: true },
    { key: 'riskLevel', label: 'Risk ban đầu', sortable: true },
    { key: 'score', label: 'Score', sortable: true, type: 'number' },
    { 
      key: 'resolutionTime', 
      label: 'Thời gian xử lý',
      render: (_, row) => {
        if (!row.resolvedDate || !row.createdDate) return '-';
        const days = Math.floor((new Date(row.resolvedDate) - new Date(row.createdDate)) / (1000 * 60 * 60 * 24));
        return `${days} ngày`;
      }
    },
    { key: 'resolvedBy', label: 'Người xử lý', sortable: true },
    { key: 'resolvedDate', label: 'Ngày xử lý', type: 'date' }
  ];

  const handleExport = () => {
    const exportData = resolvedWarnings.map(w => ({
      'Mã': w.code,
      'Khách hàng': w.customerName,
      'Risk ban đầu': w.riskLevel,
      'Score': w.score,
      'Ngày tạo': w.createdDate,
      'Ngày xử lý': w.resolvedDate,
      'Người xử lý': w.resolvedBy,
      'Hành động': w.history?.[0]?.action || '',
      'Ghi chú': w.history?.[0]?.notes || ''
    }));
    exportToCSV(exportData, 'bao-cao-da-xu-ly');
  };

  return (
    <div className="container-fluid">
      <div className="page-header-sticky">
        <div className="row mb-4">
          <div className="col-12">
            <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
            <h2>
              <i className="bi bi-check-circle me-2 text-success"></i>
              Báo cáo các trường hợp cảnh báo sớm đã đóng
            </h2>
            <p className="text-muted">Historical analysis, lessons learned</p>
          </div>
        </div>
      </div>

      {/* Branch Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          <BranchSelect 
            value={selectedBranch}
            onChange={setSelectedBranch}
            branches={branches}
          />
        </div>
      </div>

      {/* Success Metrics */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <KPICard 
            title="Tổng đã xử lý"
            value={resolvedWarnings.length}
            icon="bi-check-circle-fill"
            color="success"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Thời gian TB"
            value={`${kpis.avgResolutionTime} ngày`}
            icon="bi-clock-history"
            color="info"
            subtitle="Tất cả mức độ"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Điểm TB"
            value={kpis.avgScore}
            icon="bi-speedometer2"
            color="primary"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Cases cao đã xử lý"
            value={kpis.highRisk}
            icon="bi-trophy"
            color="warning"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-6">
          <ReportChart 
            type="bar"
            title="Thời gian xử lý trung bình theo mức rủi ro"
            data={resolutionByRisk}
            height={300}
          />
        </div>
        <div className="col-md-6">
          <ReportChart 
            type="bar"
            title="Top 5 Performers"
            data={topPerformers}
            height={300}
          />
        </div>
      </div>

      {/* Lessons Learned */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-lightbulb me-2"></i>
                Lessons Learned
              </h5>
              <ul className="mb-0">
                <li><strong>Thanh toán:</strong> Hầu hết cases được resolve bằng cách khách hàng thanh toán nợ quá hạn</li>
                <li><strong>Xác minh:</strong> Liên hệ sớm với khách hàng giúp giảm thời gian xử lý 30%</li>
                <li><strong>Điều chỉnh:</strong> Điều chỉnh hạn mức phù hợp giúp ngăn chặn tái diễn</li>
                <li><strong>Best practice:</strong> Cases được phân công ngay có tỷ lệ xử lý nhanh hơn</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Case Studies ({resolvedWarnings.length} bản ghi)</h4>
            <button className="btn btn-success" onClick={handleExport}>
              <i className="bi bi-download me-2"></i>
              Xuất Excel
            </button>
          </div>
          <ReportTable 
            data={resolvedWarnings}
            columns={tableColumns}
            onRowClick={(row) => navigate('/warnings', { state: { selectedWarning: row } })}
            pageSize={15}
          />
        </div>
      </div>
    </div>
  );
}

export default ResolvedReport;
